import { observer } from 'mobx-react-lite';
import React from 'react';
import createTradePlanCopy from '../../../../../../classes/tradePlan/createTradePlanCopy';
import { TradePlanData } from '../../../../../../classes/tradePlan/tradePlanData';
import { useTradePlanFormStore } from '../../../../../../classes/tradePlan/tradePlanProvider';
import { colors } from '../../../../../../colorVars';
import { addTradePlan } from '../../../../../../stores/websocketStore/utils/outgoingMessages/tradePlan/addtradeplanmessage/addTradePlan';
import { useWebsocketStore } from '../../../../../../stores/websocketStore/websocketProvider';
import { WebsocketStore } from '../../../../../../stores/websocketStore/websocketStore';
import { TextButton } from '../../../../../atomic/atomicComponentLibrary/buttons/textButton/TextButton';
import { MessageBox } from '../../../../../atomic/atomicComponentLibrary/messageBox/MessageBox';
import { Body4 } from '../../../../../atomic/text/Text';
import { submitTradePlan } from '../../../../charts/d3/candlestickchart/newCandleChart/chartContextMenu/BreakBounceSetup/submitTradePlan';
import { buildMarketDataKey } from '../../../../../../stores/websocketStore/utils/marketDataRouting';
import {
  getSelectedAccount,
  mapAccountsFromBalances,
  resolveBrokerageForExecution,
  resolveMarketDataBrokerage,
} from '../../../../../../utils/accountRouting';
import style from './BottomRow.module.css';
// import useUserPreferences from "../../../../settings/userprefs/UserPreferencesHook";

type Props = {
  autoSend: boolean;
  setAutoSend: (value: boolean) => void;
};
type EnterNowDirection = 'LONG' | 'SHORT';

// NOTE this is the bottom row of controls on the Chart Trading form
export const BottomRow = observer(({ autoSend, setAutoSend }: Props) => {
  const tradePlanForm: TradePlanData = useTradePlanFormStore();
  const websocketStore: WebsocketStore = useWebsocketStore();
  const { accountBalanceStore, priceDataStore } = useWebsocketStore();
  const symbol = tradePlanForm.symbol;
  const dataSource = tradePlanForm.dataSource;
  const accounts = mapAccountsFromBalances(accountBalanceStore?.balances);
  const marketDataBrokerage = resolveMarketDataBrokerage({
    accountNumber: tradePlanForm.accountNumber,
    accounts,
    currentBrokerage: tradePlanForm.brokerage,
    dataSource,
    symbol,
  });
  const executionBrokerage = resolveBrokerageForExecution({
    accountNumber: tradePlanForm.accountNumber,
    accounts,
    currentBrokerage: tradePlanForm.brokerage,
  });
  const hash = buildMarketDataKey({
    symbol,
    dataSource,
    brokerage: marketDataBrokerage,
  });
  const quote = priceDataStore.lastQuote[hash] ?? null;
  const currentPrice =
    quote?.bid != null && quote?.ask != null
      ? (Number(quote.bid) + Number(quote.ask)) / 2
      : priceDataStore.trades[hash]?.price ??
        priceDataStore.candles[hash]?.close ??
        null;
  const overrides = {}

  const validateExecutionAccount = () => {
    if (tradePlanForm.dataSource !== 'LIVE' || tradePlanForm.accountNumber === 'BACKTEST') {
      return { status: true, message: '' };
    }

    const selectedAccount = getSelectedAccount(tradePlanForm.accountNumber, accounts);
    if (!selectedAccount) {
      return {
        status: false,
        message: `Selected account ${tradePlanForm.accountNumber} is not available.`,
      };
    }

    if (selectedAccount.isClosed) {
      return {
        status: false,
        message: `Selected account ${tradePlanForm.accountNumber} is closed.`,
      };
    }

    return { status: true, message: '' };
  };

  // console.log(`evaluateQuotes is ${tradePlanForm.evaluateQuotes}`);
  // console.log(`tradePlanConfig is ${JSON.stringify(useUserPreferences().tradePlanConfig)}`);
  // console.log(`evaluateQuotes is ${useUserPreferences().tradePlanConfig.tradePlanConfig.evaluateQuotes}`);

  // TODO this causes [MobX] Since strict-mode is enabled, changing (observed) observable values without using an action is not allowed. Tried to modify: TradePlanData@19.evaluateQuotes
  // tradePlanForm.setEvaluateQuotes(useUserPreferences().tradePlanConfig.tradePlanConfig.evaluateQuotes)

  // const submitTradePlan = () => {
  //   const validationResult = tradePlanForm.validate();
  //   if (validationResult.status === false) {
  //     tradePlanForm.setValidationStatus(validationResult);
  //   } else {
  //     const tpData = tradePlanForm.returnJson();
  //     addTradePlan({ tradePlanObject: tpData });
  //     tradePlanForm.setValidationStatus({
  //       status: true,
  //       message: 'Trade Plan Submitted.',
  //     });
  //   }
  //   if (tradePlanForm.setup === 'BREAK' || tradePlanForm.setup === 'BOUNCE') {
  //     tradePlanForm.setSetup('AD_HOC');
  //   }
  //   if (tradePlanForm.entryTactics.CANDLE_BREAK) {
  //     tradePlanForm.removeEntryTactic('CANDLE_BREAK');
  //   }
  //   if (tradePlanForm.exitTactics.BREAK_PRICE) {
  //     tradePlanForm.removeExitTactic('BREAK_PRICE');
  //   }
  // };
  // TODO combine enterNow and submitTradePlan into a single unified function
  // TODO make tradePlanForm.direction an enum rather than a string

// NOTE Enter Long and Enter Short buttons on Chart Trading form (in this file) call this function
  const enterNow = (
    direction: EnterNowDirection,
    existingTradePlan: TradePlanData
  ) => {
    // make a copy
    const newInstance = createTradePlanCopy({
      tradePlanDataInstance: existingTradePlan,
      keysToOverWrite: {},
      websocketStore,
    });

    // console.log(`evaluateQuotes in EnterNow is set to ${newInstance.evaluateQuotes}`)

    newInstance.setSetup('AD_HOC');
    const quantityBasisValue = newInstance.currentQuantityBasisValue;
    newInstance.clearEntryTactics();
    newInstance.addEntryTactic('MARKET_ORDER', { quantityBasisValue });
    newInstance.clearSetupFilters();
    newInstance.setDirection(direction);

    // is this necessary?  How does this jive with setDirection above?
    console.log('direction', direction);
    // newInstance.setGoLong(direction === 'LONG');
    // newInstance.setGoShort(direction === 'SHORT');

    // validate with the current form rather than the copy
    const accountValidation = validateExecutionAccount();
    if (accountValidation.status === false) {
      existingTradePlan.setValidationStatus(accountValidation);
      return;
    }

    const validationResult = existingTradePlan.validate();
    if (validationResult.status === false) {
      existingTradePlan.setValidationStatus(validationResult);
    } else {
      const tpData = newInstance.returnJson();
      const options =
        newInstance.instrument === 'option' ||
        newInstance.instrument === 'future_option'
          ? newInstance.optionManagement.returnJson?.()
          : undefined;
      addTradePlan({ tradePlanObject: tpData, options: options });
      existingTradePlan.setValidationStatus({
        status: true,
        message: 'Trade Plan Submitted.',
      });

      // TP submitted, so start with a clean slate
      // TODO clear any Break or Bounce setup
      existingTradePlan.clearSetupFilters();
      existingTradePlan.removeEntryTactic('CANDLE_BREAK');
    }
  };
  const clearForm = () => {
    tradePlanForm.resetToDefaults();
  };
  const clearChartTactics = () => {
    tradePlanForm.setSetup('AD_HOC');
    if (tradePlanForm.entryTactics.CANDLE_BREAK) {
      tradePlanForm.removeEntryTactic('CANDLE_BREAK');
    }
    if (tradePlanForm.exitTactics.BREAK_PRICE) {
      tradePlanForm.removeExitTactic('BREAK_PRICE');
    }
    tradePlanForm.setValidationStatus({
      status: true,
      message: 'Chart tactics cleared.',
    });
  };

  // TODO use same handler for Create and Enter buttons

  // NOTE these Chart Trading buttons use createTradePlanCopy, but the Trade Plan form (TradePlanForm.jsx) does not

  const syncResolvedExecutionContext = () => {
    tradePlanForm.setBrokerage(
      tradePlanForm.dataSource === 'LIVE'
        ? marketDataBrokerage
        : executionBrokerage
    );
  };

  return (
    <div className={style.bottomRow}>
      <TextButton
        loading={false}
        clickHandler={clearChartTactics}
        margin="0 5px 0 0"
        backgroundColor="green"
        fontColor="white"
        size="small"
      >
        Clear Chart Tactics
      </TextButton>
      <MessageBox width="320px" />
      <div className={style.labelAndCheckbox}>
        <input
          className={style.checkbox}
          type="checkbox"
          checked={autoSend}
          onChange={({ target }) => setAutoSend(target.checked)}
        />
        <Body4 color={colors.lightGrey}>Auto Send on Setup</Body4>
      </div>
      <TextButton
        loading={false}
        clickHandler={() =>
          {
            syncResolvedExecutionContext();
            const accountValidation = validateExecutionAccount();
            if (accountValidation.status === false) {
              tradePlanForm.setValidationStatus(accountValidation);
              return;
            }
            submitTradePlan({ currentPrice, tradePlanForm, overrides, websocketStore });
          }
        }
        margin="0 15px 0 66px"
        backgroundColor="green"
        fontColor="white"
        size="small"
      >
        Create Trade Plan
      </TextButton>
      <TextButton
        margin="0 15px 0 0"
        backgroundColor="green"
        fontColor="white"
        size="small"
        loading={false}
        clickHandler={() => {
          syncResolvedExecutionContext();
          enterNow('LONG', tradePlanForm);
        }}
        disabled={
          tradePlanForm.direction === 'SHORT' ||
          tradePlanForm.direction === 'NONE'
        }
      >
        Enter Long
      </TextButton>
      <TextButton
        margin="0 15px 0 0"
        backgroundColor="red"
        fontColor="white"
        size="small"
        loading={false}
        clickHandler={() => {
          syncResolvedExecutionContext();
          enterNow('SHORT', tradePlanForm);
        }}
        disabled={
          tradePlanForm.direction === 'LONG' ||
          tradePlanForm.direction === 'NONE'
        }
      >
        Enter Short
      </TextButton>
      <TextButton
        loading={false}
        clickHandler={clearForm}
        margin="0 0 0 0"
        backgroundColor="blue"
        fontColor="white"
        size="small"
      >
        Clear Form
      </TextButton>
    </div>
  );
});
